import { useQuery } from '@tanstack/react-query';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { fetchMatterTimeline } from '@/lib/mattersApi';

/**
 * The matter's recorded chronology — CaseTimelineEvent rows written as things happen
 * (matter opened, hearings scheduled and decided, allocations, returns…).
 *
 * GET /matters/:id/timeline was built and security-hardened but no screen ever called it: the
 * Timeline tab assembles its own list in the browser from other endpoints. This shows the server
 * record alongside it. Only WHAT happened, WHEN and WHO is shown — never the event payload, whose
 * free text is not filtered for every event type.
 */
function eventLabel(eventType: string): string {
  const words = eventType.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function MatterCaseRecordPanel({ matterId }: { matterId: string }) {
  const q = useQuery({
    queryKey: ['matter-timeline', matterId],
    queryFn: () => fetchMatterTimeline(matterId, { limit: 50 }),
    staleTime: 30_000,
  });
  const status = (q.error as { response?: { status?: number } } | null)?.response?.status;

  return (
    <Card data-testid="case-record">
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ClockCounterClockwise className="h-4 w-4 text-muted-foreground" />
          Case record
        </div>
        {q.data && (
          <div className="text-xs text-muted-foreground">
            {q.data.pagination.total > q.data.events.length
              ? `Latest ${q.data.events.length} of ${q.data.pagination.total}`
              : `${q.data.pagination.total} recorded events`}
          </div>
        )}
      </div>
      <CardContent className="p-0">
        {q.isLoading ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">Loading the case record…</div>
        ) : q.isError ? (
          status === 403 || status === 404
            ? <div className="px-6 py-6 text-sm text-muted-foreground">The case record is not available to you.</div>
            : <QueryErrorState compact what="the case record" onRetry={() => void q.refetch()} />
        ) : !q.data || q.data.events.length === 0 ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">Nothing has been recorded on this matter yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {q.data.events.map((e) => (
              <li key={e.id} data-testid="case-record-event" className="flex items-baseline justify-between gap-4 px-6 py-2.5 text-sm">
                <span className="text-foreground">{eventLabel(e.eventType)}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {e.actor ? `${e.actor.name} · ` : ''}
                  {new Date(e.occurredAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
